import * as React from "react";
import * as RechartsPrimitive from "recharts";
import { cn } from "@/lib/utils";

/**
 * shadcn-style chart primitives. Thin wrapper over Recharts that:
 * - Provides a `ChartContainer` carrying CSS-variable-driven theme colors
 *   per series.
 * - Adds a `ChartTooltipContent` styled to match our design system
 *   (rounded card, muted-foreground labels).
 *
 * Usage:
 *   const config = {
 *     bookings: { label: "Bookings", color: "hsl(var(--chart-1))" },
 *   } satisfies ChartConfig;
 *
 *   <ChartContainer config={config} className="h-72 w-full">
 *     <AreaChart data={data}>
 *       <CartesianGrid vertical={false} />
 *       <XAxis dataKey="date" />
 *       <YAxis />
 *       <ChartTooltip content={<ChartTooltipContent />} />
 *       <Area dataKey="bookings" fill="var(--color-bookings)" />
 *     </AreaChart>
 *   </ChartContainer>
 */

export type ChartConfig = {
  [k: string]: {
    label?: React.ReactNode;
    icon?: React.ComponentType;
    color?: string;
  };
};

type ChartContextProps = {
  config: ChartConfig;
};

const ChartContext = React.createContext<ChartContextProps | null>(null);

function useChart() {
  const ctx = React.useContext(ChartContext);
  if (!ctx) throw new Error("useChart must be used within <ChartContainer />");
  return ctx;
}

const ChartContainer = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & {
    config: ChartConfig;
    children: React.ComponentProps<
      typeof RechartsPrimitive.ResponsiveContainer
    >["children"];
  }
>(({ id, className, children, config, ...props }, ref) => {
  const uniqueId = React.useId();
  const chartId = `chart-${id ?? uniqueId.replace(/:/g, "")}`;

  return (
    <ChartContext.Provider value={{ config }}>
      <div
        data-chart={chartId}
        ref={ref}
        className={cn(
          "flex aspect-video justify-center text-xs",
          // Recharts overrides — keep gridlines + axes light, match our muted palette.
          "[&_.recharts-cartesian-grid_line[stroke='#ccc']]:stroke-border/40",
          "[&_.recharts-cartesian-axis-tick_text]:fill-muted-foreground",
          "[&_.recharts-tooltip-cursor]:fill-muted/30",
          className,
        )}
        {...props}
      >
        <ChartStyle id={chartId} config={config} />
        <RechartsPrimitive.ResponsiveContainer>
          {children}
        </RechartsPrimitive.ResponsiveContainer>
      </div>
    </ChartContext.Provider>
  );
});
ChartContainer.displayName = "ChartContainer";

/** Inject `--color-<key>: <hex>` for each series so chart elements can
 *  reference `var(--color-bookings)` etc. directly in JSX. */
const ChartStyle = ({ id, config }: { id: string; config: ChartConfig }) => {
  const entries = Object.entries(config).filter(([, v]) => v.color);
  if (entries.length === 0) return null;
  const css = `[data-chart=${id}] {\n${entries
    .map(([k, v]) => `  --color-${k}: ${v.color};`)
    .join("\n")}\n}`;
  return <style dangerouslySetInnerHTML={{ __html: css }} />;
};

const ChartTooltip = RechartsPrimitive.Tooltip;

type TooltipPayloadItem = {
  value?: number | string;
  name?: string;
  dataKey?: string;
  payload?: Record<string, unknown>;
  color?: string;
};

const ChartTooltipContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & {
    active?: boolean;
    payload?: TooltipPayloadItem[];
    label?: React.ReactNode;
    labelFormatter?: (
      value: React.ReactNode,
      payload: TooltipPayloadItem[] | undefined,
    ) => React.ReactNode;
    valueFormatter?: (value: number | string | undefined) => React.ReactNode;
    indicator?: "dot" | "line";
    hideLabel?: boolean;
  }
>(
  (
    {
      active,
      payload,
      label,
      labelFormatter,
      valueFormatter,
      indicator = "dot",
      hideLabel = false,
      className,
    },
    ref,
  ) => {
    const { config } = useChart();
    if (!active || !payload || payload.length === 0) return null;

    return (
      <div
        ref={ref}
        className={cn(
          "rounded-md border bg-popover px-3 py-2 text-xs shadow-md text-popover-foreground",
          className,
        )}
      >
        {!hideLabel && (
          <div className="mb-1 text-muted-foreground">
            {labelFormatter ? labelFormatter(label, payload) : label}
          </div>
        )}
        <div className="grid gap-1">
          {payload.map((item, i) => {
            const key = item.dataKey ?? item.name ?? `series-${i}`;
            const seriesLabel = config[key]?.label ?? item.name ?? key;
            const color = item.color ?? config[key]?.color;
            return (
              <div
                key={key}
                className="flex items-center gap-2 [&>svg]:h-2.5 [&>svg]:w-2.5"
              >
                <span
                  className={cn(
                    "shrink-0 rounded-[2px]",
                    indicator === "dot" ? "h-2 w-2 rounded-full" : "h-3 w-1",
                  )}
                  style={{ background: color }}
                />
                <span className="text-muted-foreground">{seriesLabel}</span>
                <span className="ml-auto font-medium tabular-nums">
                  {valueFormatter
                    ? valueFormatter(item.value)
                    : item.value ?? "—"}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  },
);
ChartTooltipContent.displayName = "ChartTooltipContent";

export { ChartContainer, ChartTooltip, ChartTooltipContent };
